import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, DestroyRef, inject, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControlOptions } from '@angular/forms';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxCustomAddress, OnyxTime } from '@onyx/angular';
import {
  OnyxAddressInputComponent,
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxDividerComponent,
  OnyxFormGroupComponent,
  OnyxIconComponent,
  onyxMaxDateTimeValidator,
  OnyxMessageComponent,
  onyxMinDateTimeValidator,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxSuggestionsComponent,
  OnyxTimepickerComponent,
  OnyxToastService,
  OnyxToggleComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { omit } from 'lodash';
import { DateTime } from 'luxon';
import { forkJoin, Subject } from 'rxjs';
import { DAY_TIME_RANGE } from '../../../common/constants/common/day-time-range';
import { SuggestionHelper } from '../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../common/helpers/validation.helper';
import type { TelematicsItem } from '../../../common/interfaces/telematics/telematics-item';
import { FleetIdentifierPipe } from '../../../common/pipes/fleet-identifier.pipe';
import { JoinPipe } from '../../../common/pipes/join.pipe';
import type {
  Delegation,
  SimplifiedDelegation,
} from '../common/interfaces/delegation';
import { DelegationsService } from '../common/services/delegations.service';

export type DelegationActionModalFormDto = Omit<
  DelegationActionModalForm,
  'selected'
>;

type DelegationActionModalFormGroup = ReturnType<
  DelegationActionModalComponent['buildDelegationForm']
>;

export type DelegationActionModalForm = ReturnType<
  DelegationActionModalFormGroup['getRawValue']
>;

export interface DelegationActionModalData {
  mode: DelegationActionModalMode;
  delegations: Delegation[] | SimplifiedDelegation[];
  telematicsItem?: TelematicsItem;
}

export enum DelegationActionModalMode {
  ACTIVATE = 'activate',
  FINISH = 'finish',
  SETTLE = 'settle',
  RETURN = 'return',
}

@Component({
  selector: 'app-delegation-action-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    JoinPipe,
    FleetIdentifierPipe,
    OnyxDatepickerComponent,
    OnyxTimepickerComponent,
    OnyxAddressInputComponent,
    OnyxButtonComponent,
    ReactiveFormsModule,
    OnyxSuggestionsComponent,
    OnyxFormGroupComponent,
    OnyxDividerComponent,
    OnyxToggleComponent,
    OnyxMessageComponent,
    OnyxTooltipDirective,
    OnyxIconComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './delegation-action-modal.component.html',
  styleUrl: './delegation-action-modal.component.scss',
})
export class DelegationActionModalComponent {
  protected readonly data = inject<DelegationActionModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(OnyxToastService);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly delegationsService = inject(DelegationsService);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'delegations';
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
    { future: false },
  );
  protected readonly TIME_SUGGESTIONS = SuggestionHelper.getTimeSuggestions();
  protected readonly TODAY = DateTime.now().toISODate();
  protected readonly HEADING: string = {
    [DelegationActionModalMode.ACTIVATE]: 'labels.activateDelegation',
    [DelegationActionModalMode.FINISH]: 'labels.finishDelegation',
    [DelegationActionModalMode.SETTLE]: `${this.I18N}.settleDelegation`,
    [DelegationActionModalMode.RETURN]: `${this.I18N}.returnDelegation`,
  }[this.data.mode];
  protected readonly SUBMIT_LABEL: string = {
    [DelegationActionModalMode.ACTIVATE]: 'labels.activate',
    [DelegationActionModalMode.FINISH]: 'labels.finish',
    [DelegationActionModalMode.SETTLE]: 'labels.settle',
    [DelegationActionModalMode.RETURN]: 'labels.save',
  }[this.data.mode];

  protected readonly isActivate =
    this.data.mode === DelegationActionModalMode.ACTIVATE;
  protected readonly isFinish =
    this.data.mode === DelegationActionModalMode.FINISH;
  protected readonly isSettle =
    this.data.mode === DelegationActionModalMode.SETTLE;

  protected readonly drivers = this.data.delegations.map(
    ({ primaryDriver, secondaryDriver }) =>
      [primaryDriver, secondaryDriver]
        .filter((driver) => !!driver)
        .map(({ driverData }) => this.namePipe.transform(driverData)),
  );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  constructor() {
    for (const [index, delegation] of this.data.delegations.entries()) {
      const delegationPeriodData = this.isActivate
        ? delegation.start
        : delegation.end;

      const control = this.form.controls[index];
      if (delegationPeriodData) control.patchValue(delegationPeriodData);

      control.controls.selected.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((selected) => {
          const { date, time, address } = control.controls;
          ValidationHelper.toggleRequiredValidator(
            [date, time, address],
            selected,
          );
        });
    }
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const requests$ = this.form.controls
      .map((control, index) => ({
        uuid: this.data.delegations[index].uuid,
        control,
      }))
      .filter(({ control }) => control.value.selected)
      .map(({ uuid, control }) => {
        const dto = omit(control.getRawValue(), 'selected');

        return {
          [DelegationActionModalMode.ACTIVATE]:
            this.delegationsService.activateDelegation(uuid, dto),
          [DelegationActionModalMode.FINISH]:
            this.delegationsService.finishDelegation(uuid, dto),
          [DelegationActionModalMode.SETTLE]:
            this.delegationsService.settleDelegation(uuid),
          [DelegationActionModalMode.RETURN]:
            this.delegationsService.returnDelegation(uuid, dto),
        }[this.data.mode];
      });
    if (!requests$.length) return;

    this.loading.set(true);
    forkJoin(requests$)
      .subscribe(() => {
        const message = {
          [DelegationActionModalMode.ACTIVATE]: `${this.I18N}.delegationActivated`,
          [DelegationActionModalMode.FINISH]: `${this.I18N}.delegationFinished`,
          [DelegationActionModalMode.SETTLE]: `${this.I18N}.delegationSettled`,
          [DelegationActionModalMode.RETURN]: `${this.I18N}.delegationReturn`,
        }[this.data.mode];

        const description = (
          {
            [DelegationActionModalMode.RETURN]: `${this.I18N}.delegationReturnDescription`,
          } as Partial<Record<DelegationActionModalMode, string>>
        )[this.data.mode];

        this.toastService.showSuccess({
          message,
          description,
          keepOnNavigation: true,
        });
        this.close$.next();
      })
      .add(() => this.loading.set(false));
  }

  protected buildForm() {
    return this.fb.array(
      this.data.delegations.map((delegation) =>
        this.buildDelegationForm(delegation),
      ),
    );
  }

  private buildDelegationForm(delegation: Delegation | SimplifiedDelegation) {
    let maxDateTime: DateTime | null = null;
    let minDateTime: DateTime | null = null;

    if (this.isActivate) {
      maxDateTime = DateTime.now();

      if (delegation.end.date) {
        const { date, time } = delegation.end;
        const endDateTime = DateTime.fromISO(
          `${date}T${time ?? DAY_TIME_RANGE.to}`,
        ).minus({
          minute: 1,
        });

        maxDateTime = DateTime.min(maxDateTime, endDateTime);
      }
    }

    if (!this.isActivate) {
      const { date, time } = delegation.start;
      minDateTime = DateTime.fromISO(`${date}T${time}`).plus({ minute: 1 });
      maxDateTime = DateTime.now();
    }

    return this.fb.group(
      {
        selected: this.fb.control(true),
        date: this.fb.control<string | null>(null, [Validators.required]),
        time: this.fb.control<OnyxTime | null>(null, [Validators.required]),
        address: this.fb.control<OnyxCustomAddress | null>(null, [
          Validators.required,
        ]),
      },
      {
        validators: [
          ...(maxDateTime ? [onyxMaxDateTimeValidator(maxDateTime)] : []),
          ...(minDateTime ? [onyxMinDateTimeValidator(minDateTime)] : []),
        ],
      } as AbstractControlOptions,
    );
  }
}
